/**
 * ProjectTypeRail.tsx
 * Sovereign-Emerald left rail for the Asset Catalog screen: a searchable,
 * selectable list of Project Types with add / edit / toggle controls.
 * Presentational + selection only — all data work is wired by the page.
 */

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { FolderTree, Plus, Pencil, Power, Search } from 'lucide-react';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import type { ProjectType } from '@/services/projectTypeService';

type Category = NonNullable<ProjectType['category']>;

const CATEGORY_STYLE: Record<Category, { bg: string; border: string; color: string; label: string }> = {
  solar: { bg: 'rgba(201,169,97,0.14)', border: 'rgba(201,169,97,0.34)', color: '#8A6D2B', label: 'Solar' },
  wind: { bg: 'rgba(51,146,116,0.12)', border: 'rgba(51,146,116,0.30)', color: SOVEREIGN.brand, label: 'Wind' },
  hybrid: { bg: 'rgba(31,90,71,0.10)', border: 'rgba(31,90,71,0.28)', color: SOVEREIGN.bottle, label: 'Hybrid' },
  bess: { bg: 'rgba(38,79,60,0.10)', border: 'rgba(38,79,60,0.30)', color: SOVEREIGN.btn, label: 'BESS' },
};

function CategoryChip({ category }: { category?: ProjectType['category'] }) {
  const s = category ? CATEGORY_STYLE[category] : undefined;
  if (!s) return null;
  return (
    <span
      className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
    >
      {s.label}
    </span>
  );
}

export interface ProjectTypeRailProps {
  types: ProjectType[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (v: string) => void;
  onAdd: () => void;
  onEdit: (type: ProjectType) => void;
  onToggle: (type: ProjectType) => void;
  loading?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  /** asset count per project-type id, optional */
  assetCounts?: Record<string, number>;
}

export default function ProjectTypeRail({
  types,
  selectedId,
  onSelect,
  search,
  onSearch,
  onAdd,
  onEdit,
  onToggle,
  loading,
  canCreate,
  canUpdate,
  assetCounts,
}: ProjectTypeRailProps) {
  const sorted = useMemo(
    () => [...types].sort((a, b) => a.name.localeCompare(b.name)),
    [types],
  );

  return (
    <motion.aside
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-2xl flex flex-col"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute"
        style={{ inset: 7, border: `1px dashed ${SOVEREIGN.innerRule}`, borderRadius: 11 }}
      />

      {/* Header */}
      <header className="relative flex items-center gap-3 px-5 pt-5">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: 'rgba(201,169,97,0.16)', color: SOVEREIGN.bottle }}
        >
          <FolderTree size={18} aria-hidden />
        </span>
        <div className="mr-auto">
          <h2 className="text-base font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>
            Project Types
          </h2>
          <p className="text-xs" style={{ color: SOVEREIGN.kpiCaption }}>
            {sorted.length} {sorted.length === 1 ? 'type' : 'types'}
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            title="Add project type"
            aria-label="Add project type"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white transition-colors"
            style={{
              background: SOVEREIGN.btn,
              border: `1px solid ${SOVEREIGN.btnBorder}`,
              boxShadow: SOVEREIGN.btnShadow,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = SOVEREIGN.btnHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = SOVEREIGN.btn; }}
          >
            <Plus size={14} aria-hidden /> Add
          </button>
        )}
      </header>

      {/* Search */}
      <div className="relative px-5 pt-4">
        <div className="relative">
          <Search
            size={15}
            aria-hidden
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: SOVEREIGN.kpiCaption }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search types…"
            className="w-full text-sm rounded-lg pl-9 pr-3 py-2 outline-none transition-colors"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.ink,
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = SOVEREIGN.inputHoverBorder; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = SOVEREIGN.inputBorder; }}
          />
        </div>
      </div>

      {/* List */}
      <div className="relative mt-3 px-3 pb-3 flex-1 overflow-y-auto" style={{ maxHeight: '70vh' }}>
        {loading && sorted.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
            Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="px-3 py-10 text-center text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
            No project types found.
          </div>
        ) : (
          <ul className="space-y-1">
            {sorted.map((type) => {
              const id = type._id || type.id || '';
              const selected = id === selectedId;
              const isActive = (type.status ?? type.projectTypeStatus) === 'active';
              const count = assetCounts?.[id];
              return (
                <li key={id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(id); } }}
                    className="group relative w-full text-left rounded-xl px-3 py-2.5 cursor-pointer transition-colors"
                    style={{
                      background: selected ? 'rgba(31,90,71,0.08)' : 'transparent',
                      border: `1px solid ${selected ? 'rgba(31,90,71,0.28)' : 'transparent'}`,
                    }}
                    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'rgba(11,59,46,0.04)'; }}
                    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {selected && (
                      <span
                        aria-hidden
                        className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1 rounded-r"
                        style={{ background: SOVEREIGN.champagne }}
                      />
                    )}
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        aria-hidden
                        className="h-2 w-2 rounded-full shrink-0"
                        title={isActive ? 'Active' : 'Inactive'}
                        style={{ background: isActive ? SOVEREIGN.brand : SOVEREIGN.kpiCaption }}
                      />
                      <span
                        className={`text-sm font-semibold truncate ${isActive ? '' : 'line-through'}`}
                        style={{ color: isActive ? SOVEREIGN.ink : SOVEREIGN.kpiCaption }}
                      >
                        {type.name}
                      </span>
                      <span className="ml-auto flex items-center gap-1.5 shrink-0">
                        {typeof count === 'number' && (
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ background: SOVEREIGN.barTrack, color: SOVEREIGN.bottle }}
                            title={`${count} assets`}
                          >
                            {count}
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="mt-1.5 flex items-center gap-2 pl-4">
                      <CategoryChip category={type.category} />
                      {canUpdate && (
                        <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            title="Edit project type"
                            aria-label={`Edit ${type.name}`}
                            onClick={(e) => { e.stopPropagation(); onEdit(type); }}
                            className="inline-flex items-center justify-center w-6 h-6 rounded transition-colors"
                            style={{ color: SOVEREIGN.kpiCaption }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = SOVEREIGN.bottle; e.currentTarget.style.background = 'rgba(31,90,71,0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = SOVEREIGN.kpiCaption; e.currentTarget.style.background = 'transparent'; }}
                          >
                            <Pencil size={13} aria-hidden />
                          </button>
                          <button
                            type="button"
                            title={isActive ? 'Deactivate' : 'Activate'}
                            aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${type.name}`}
                            onClick={(e) => { e.stopPropagation(); onToggle(type); }}
                            className="inline-flex items-center justify-center w-6 h-6 rounded transition-colors"
                            style={{ color: isActive ? '#9F1239' : SOVEREIGN.brand }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = isActive ? 'rgba(159,18,57,0.08)' : 'rgba(51,146,116,0.10)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <Power size={13} aria-hidden />
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </motion.aside>
  );
}
